import { Suspense, useEffect, useState, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { lazyNamed } from "./lib/lazy.ts";
import { LastInspectionLazy } from "./screens/lastInspectionLazy.ts";
import { AuthProvider, useAuth } from "./auth.tsx";
import { Layout, More, RequireRole, RequireSwitch } from "./components/Layout.tsx";
import { Button, ErrorBox, Skeleton, ToastProvider, useToast } from "./components/ui.tsx";
import { ApiProvider, SessionProvider, useApi, useSession, useSessionState } from "./session.tsx";
import { isPlanning, VOTE_WORDS } from "@logwave/core";
import { ForYou } from "./screens/ForYou.tsx";
const CheckIn = lazyNamed(() => import("./screens/CheckIn.tsx"), "CheckIn");
const GoogleCallback = lazyNamed(() => import("./components/GoogleMaps.tsx"), "GoogleCallback");
const Do = lazyNamed(() => import("./screens/Do.tsx"), "Do");
const Fix = lazyNamed(() => import("./screens/Do.tsx"), "Fix");
// "Fix and log it now" lands here (core fixgo.ts; screens/FixArrive.tsx): the item's plan, step 1 open, one tap to log it.
const FixArrive = lazyNamed(() => import("./screens/FixArrive.tsx"), "FixArrive");
const InviteAccept = lazyNamed(() => import("./screens/InviteAccept.tsx"), "InviteAccept");
const JoinCode = lazyNamed(() => import("./screens/Join.tsx"), "JoinCode");
const Welcome = lazyNamed(() => import("./screens/Welcome.tsx"), "Welcome");
const Connect = lazyNamed(() => import("./screens/Connect.tsx"), "Connect");
const Kiosk = lazyNamed(() => import("./screens/Kiosk.tsx"), "Kiosk");
const RoomBoardScreen = lazyNamed(() => import("./screens/RoomBoard.tsx"), "RoomBoardScreen");
const HealthScreen = lazyNamed(() => import("./screens/Health.tsx"), "HealthScreen");
const RoomCheckScreen = lazyNamed(() => import("./screens/RoomCheck.tsx"), "RoomCheckScreen");
const AiRoomCheckScreen = lazyNamed(() => import("./screens/AiRoomCheck.tsx"), "AiRoomCheckScreen");
const RoomContentsScreen = lazyNamed(() => import("./screens/RoomCheck.tsx"), "RoomContentsScreen");
const CleanPlaceScreen = lazyNamed(() => import("./screens/CleanPlace.tsx"), "CleanPlaceScreen");
const Opening = lazyNamed(() => import("./screens/Opening.tsx"), "Opening");
const GettingReady = lazyNamed(() => import("./screens/Journey.tsx"), "GettingReady");
const WeAreOpen = lazyNamed(() => import("./screens/Journey.tsx"), "WeAreOpen");
const StageQuestion = lazyNamed(() => import("./screens/Journey.tsx"), "StageQuestion");
const SetupReady = lazyNamed(() => import("./screens/SetupReady.tsx"), "SetupReady");
const HaveWalk = lazyNamed(() => import("./screens/Journey.tsx"), "HaveWalk");
const KidMode = lazyNamed(() => import("./screens/home/KidMode.tsx"), "KidMode");
const AddPerson = lazyNamed(() => import("./screens/Invite.tsx"), "AddPerson");
import { useKiosk } from "./lib/kiosk.ts";
import { labelTokenInPath, pendingLabel, rememberLabel } from "./lib/labelScan.ts";
const HowToPage = lazyNamed(() => import("./screens/Learn.tsx"), "HowToPage");
const Learn = lazyNamed(() => import("./screens/Learn.tsx"), "Learn");
const LessonPage = lazyNamed(() => import("./screens/Learn.tsx"), "LessonPage");
const Library = lazyNamed(() => import("./screens/Learn.tsx"), "Library");
const LibraryTopic = lazyNamed(() => import("./screens/Learn.tsx"), "LibraryTopic");
const SignPage = lazyNamed(() => import("./screens/Learn.tsx"), "SignPage");
const PublicSheetScreen = lazyNamed(() => import("./screens/PublicSheet.tsx"), "PublicSheetScreen");
const SheetAbout = lazyNamed(() => import("./screens/PublicSheet.tsx"), "SheetAbout");
const SignedInSheet = lazyNamed(() => import("./screens/PublicSheet.tsx"), "SignedInSheet");
const ScanLabel = lazyNamed(() => import("./screens/ScanLabel.tsx"), "ScanLabel");
const FoodAct = lazyNamed(() => import("./screens/ScanLabel.tsx"), "FoodAct");
const Paper = lazyNamed(() => import("./screens/Paper.tsx"), "Paper");
const Review = lazyNamed(() => import("./screens/Paper.tsx"), "Review");
const People = lazyNamed(() => import("./screens/People.tsx"), "People");
const Person = lazyNamed(() => import("./screens/People.tsx"), "Person");
const Playbook = lazyNamed(() => import("./screens/Playbook.tsx"), "Playbook");
const Prove = lazyNamed(() => import("./screens/Prove.tsx"), "Prove");
const AuditTrail = lazyNamed(() => import("./screens/AuditTrail.tsx"), "AuditTrail");
const Signs = lazyNamed(() => import("./screens/Signs.tsx"), "Signs");
const HealthAgreementRoster = lazyNamed(() => import("./screens/HealthAgreement.tsx"), "HealthAgreementRoster");
const Report = lazyNamed(() => import("./screens/Report.tsx"), "Report");
const Settings = lazyNamed(() => import("./screens/Settings.tsx"), "Settings");
const Recall = lazyNamed(() => import("./screens/Recall.tsx"), "Recall");
const AllergyOrder = lazyNamed(() => import("./screens/Allergens.tsx"), "AllergyOrder");
const AllergenSetup = lazyNamed(() => import("./screens/Allergens.tsx"), "AllergenSetup");
const AllergenDish = lazyNamed(() => import("./screens/Allergens.tsx"), "AllergenDish");
const AllergenMenuScreen = lazyNamed(() => import("./screens/Allergens.tsx"), "AllergenMenuScreen");
const MenuPhoto = lazyNamed(() => import("./screens/MenuPhoto.tsx"), "MenuPhoto");
const PublicAllergenMenuScreen = lazyNamed(() => import("./screens/Allergens.tsx"), "PublicAllergenMenuScreen");
const Content = lazyNamed(() => import("./screens/Content.tsx"), "Content");
const Messages = lazyNamed(() => import("./screens/Messages.tsx"), "Messages");
const Privacy = lazyNamed(() => import("./screens/Privacy.tsx"), "Privacy");
const Compliance = lazyNamed(() => import("./screens/Compliance.tsx"), "Compliance");
const DataDashboard = lazyNamed(() => import("./screens/DataDashboard.tsx"), "DataDashboard");
const Setup = lazyNamed(() => import("./screens/Setup.tsx"), "Setup");
const WaitlistAdmin = lazyNamed(() => import("./screens/WaitlistAdmin.tsx"), "WaitlistAdmin");
import { SignIn } from "./screens/SignIn.tsx";
import { Today } from "./screens/Today.tsx";
const Team = lazyNamed(() => import("./screens/Team.tsx"), "Team");
const ActivityScreen = lazyNamed(() => import("./screens/TeamWork.tsx"), "ActivityScreen");
const CloseOfDayScreen = lazyNamed(() => import("./screens/TeamWork.tsx"), "CloseOfDayScreen");
const LogReviewScreen = lazyNamed(() => import("./screens/TeamWork.tsx"), "LogReviewScreen");
const PointsScreen = lazyNamed(() => import("./screens/TeamWork.tsx"), "PointsScreen");
const Me = lazyNamed(() => import("./screens/Me.tsx"), "Me");
const Reach = lazyNamed(() => import("./screens/Reach.tsx"), "Reach");
const Notifications = lazyNamed(() => import("./screens/Notifications.tsx"), "Notifications");
const Workspaces = lazyNamed(() => import("./screens/Workspaces.tsx"), "Workspaces");
const InviteBusiness = lazyNamed(() => import("./screens/InviteBusiness.tsx"), "InviteBusiness");
const PlanBillingPage = lazyNamed(() => import("./screens/PlanBilling.tsx"), "PlanBillingPage");
const Permissions = lazyNamed(() => import("./screens/Permissions.tsx"), "Permissions");
const OwnershipAccept = lazyNamed(() => import("./screens/Permissions.tsx"), "OwnershipAccept");
const CancelPlanPage = lazyNamed(() => import("./screens/PlanBilling.tsx"), "CancelPlanPage");
const WinbackPage = lazyNamed(() => import("./screens/PlanBilling.tsx"), "WinbackPage");
const AddLocation = lazyNamed(() => import("./screens/AddLocation.tsx"), "AddLocation");
import { BillingDone, BillingStart } from "./components/Billing.tsx";
const SurfacePage = lazyNamed(() => import("./screens/Surfaces.tsx"), "SurfacePage");
const CleanupList = lazyNamed(() => import("./screens/Cleanups.tsx"), "CleanupList");
const CleanupPage = lazyNamed(() => import("./screens/Cleanups.tsx"), "CleanupPage");
const Surfaces = lazyNamed(() => import("./screens/Surfaces.tsx"), "Surfaces");
const Kits = lazyNamed(() => import("./screens/Kits.tsx"), "Kits");
const PlaceObjectPage = lazyNamed(() => import("./screens/Places.tsx"), "PlaceObjectPage");
const Places = lazyNamed(() => import("./screens/Places.tsx"), "Places");
const PublicGuideScreen = lazyNamed(() => import("./screens/Things.tsx"), "PublicGuideScreen");
const ThingGuideScreen = lazyNamed(() => import("./screens/Things.tsx"), "ThingGuideScreen");
const ThingPageScreen = lazyNamed(() => import("./screens/Things.tsx"), "ThingPageScreen");
const ThingRunScreen = lazyNamed(() => import("./screens/Things.tsx"), "ThingRunScreen");
const ThingStatesScreen = lazyNamed(() => import("./screens/Things.tsx"), "ThingStatesScreen");
const ThingsScreen = lazyNamed(() => import("./screens/Things.tsx"), "ThingsScreen");
const RoomWalkScreen = lazyNamed(() => import("./screens/RoomWalk.tsx"), "RoomWalkScreen");
const WalkReviewScreen = lazyNamed(() => import("./screens/WalkReview.tsx"), "WalkReviewScreen");
const SeePlaceScreen = lazyNamed(() => import("./screens/SeePlace.tsx"), "SeePlaceScreen");
const PrepPlaceScreen = lazyNamed(() => import("./screens/PrepPlace.tsx"), "PrepPlaceScreen");
const Food = lazyNamed(() => import("./screens/Food.tsx"), "Food");
const LabelFood = lazyNamed(() => import("./screens/LabelFood.tsx"), "LabelFood");
const Cooling = lazyNamed(() => import("./screens/Cooling.tsx"), "Cooling");
const ShopKit = lazyNamed(() => import("./screens/ShopKit.tsx"), "ShopKit");
const Products = lazyNamed(() => import("./screens/Products.tsx"), "Products");
const Order = lazyNamed(() => import("./screens/Order.tsx"), "Order");
const GuestNotes = lazyNamed(() => import("./screens/GuestNotes.tsx"), "GuestNotes");
const Carrying = lazyNamed(() => import("./screens/Carrying.tsx"), "Carrying");
const StepsLink = lazyNamed(() => import("./screens/StepsLink.tsx"), "StepsLink");
const SuppliesSetup = lazyNamed(() => import("./screens/SuppliesSetup.tsx"), "SuppliesSetup");
const BuyByRoomScreen = lazyNamed(() => import("./screens/SuppliesHub.tsx"), "BuyByRoomScreen");
const StockCheckScreen = lazyNamed(() => import("./screens/SuppliesHub.tsx"), "StockCheckScreen");
const SuppliesHub = lazyNamed(() => import("./screens/SuppliesHub.tsx"), "SuppliesHub");
const SupplyPage = lazyNamed(() => import("./screens/SuppliesHub.tsx"), "SupplyPage");
const Insights = lazyNamed(() => import("./screens/Insights.tsx"), "Insights");
const HomeToday = lazyNamed(() => import("./screens/home/HomeToday.tsx"), "HomeToday");
const HomeTune = lazyNamed(() => import("./screens/home/HomeSetup.tsx"), "HomeTune");
const Have = lazyNamed(() => import("./screens/home/Have.tsx"), "Have");
const HaveArrive = lazyNamed(() => import("./screens/HaveArrive.tsx"), "HaveArrive");
const Pantry = lazyNamed(() => import("./screens/home/Pantry.tsx"), "Pantry");
const HomeGuide = lazyNamed(() => import("./screens/home/Rooms.tsx"), "HomeGuide");
const Rooms = lazyNamed(() => import("./screens/home/Rooms.tsx"), "Rooms");
const WhereItGoes = lazyNamed(() => import("./screens/home/Rooms.tsx"), "WhereItGoes");
import { isHomeOrg } from "./api.ts";
import { LinkReportHost } from "./components/LinkReport.tsx";
import { CookieBanner } from "./components/CookieBanner.tsx";
import { makeMeasure } from "./lib/consent.ts";
import { config } from "./env.ts";
import { ScreenBoundary, screenName } from "./components/ScreenError.tsx";
import { OutboxProvider } from "./lib/outbox.tsx";
import { markSignedOut, usePendingHave, type PendingHave } from "./lib/haveIntent.ts";
import { clearGo, pendingGo, type PendingGo } from "./lib/goIntent.ts";
import { fixShown, pendingFix } from "./lib/fixIntent.ts";
import { clearVote, pendingVote } from "./lib/voteIntent.ts";
import { useApiMaybe } from "./session.tsx";
import { DemoTapGate } from "./components/DemoTapGate.tsx";
import { lockedPathOk } from "./lib/lockedPaths.ts";
// The lock screen loads on demand (rare; keeps the main chunk inside its budget).
const LockScreen = lazyNamed(() => import("./components/PlanGate.tsx"), "LockScreen");
import { useBuyIntent, useFinishSetup } from "./lib/buyIntent.ts";
import { demoCarry, carryMeaningful } from "./lib/demoCarry.ts";
const BuySetup = lazyNamed(() => import("./screens/BuySetup.tsx"), "BuySetup");
const BuyCard = lazyNamed(() => import("./screens/BuySetup.tsx"), "BuyCard");

/** Today's index: a place getting ready to open gets "Getting ready" (no late alarms, review A11); else the feed (eager). */
function TodayIndex() {
  const s = useSession();
  return isPlanning(s.location) ? <GettingReady /> : <ForYou />;
}

/** A home's pages (docs/HOME.md): no legal logs, binder, opening gate or staff roles. */
export function HomeRoutes() {
  return (
    <Routes>
      <Route path="workspaces/new/business" element={<Setup start="business" />} />
      {/* A child's turn (review C2): full screen, no tab bar and no Ask button, so a child can't wander to shopping or chat. */}
      <Route path="kids" element={<Suspense fallback={<Skeleton rows={3} />}><div className="page-solo"><KidMode /></div></Suspense>} />
      <Route element={<Layout />}>
        <Route index element={<HomeToday />} />
        {/* Onboarding v2 (core journey.ts): the walk-through, the stage question and a child's turn. */}
        <Route path="start/have" element={<HaveWalk />} />
        <Route path="start/stage" element={<StageQuestion />} />
        <Route path="home-tune" element={<HomeTune />} />
        <Route path="workspaces" element={<Workspaces />} />
        <Route path="prep" element={<PrepPlaceScreen />} />
        <Route path="prep/:orgId" element={<PrepPlaceScreen />} />
        <Route path="content" element={<Content />} />
        {/* Your data and privacy (routes/compliance.ts): download my data, my choices, delete my account. */}
        <Route path="privacy" element={<Privacy />} />
        {/* The admin Data dashboard: the server gates it (DATA_ADMINS, verified sign-in); the link shows only when me.dataAdmin. */}
        <Route path="admin/data" element={<DataDashboard />} />
        <Route path="have" element={<Have />} />
        <Route path="products" element={<Products />} />
        {/* One food list at home (LAUNCH-PROPOSALS #10): the Food tab is the pantry the score reads; old links follow it. */}
        <Route path="food" element={<Pantry />} />
        <Route path="food/label" element={<LabelFood />} />
        <Route path="pantry" element={<Navigate to="/food" replace />} />
        <Route path="rooms" element={<Rooms />} />
        <Route path="rooms/where" element={<WhereItGoes />} />
        <Route path="rooms/guide/:sopId" element={<HomeGuide />} />
        <Route path="rooms/check/:room" element={<RoomCheckScreen />} />
        <Route path="rooms/check/:room/contents" element={<RoomContentsScreen />} />
        <Route path="clean" element={<CleanPlaceScreen />} />
        <Route path="health" element={<HealthScreen />} />
        <Route path="kits" element={<Kits />} />
        <Route path="kits/:kitId" element={<Kits />} />
        <Route path="invite-business" element={<InviteBusiness />} />
        <Route path="objects" element={<ThingsScreen />} />
        <Route path="objects/states" element={<ThingStatesScreen />} />
        <Route path="objects/guide" element={<ThingGuideScreen />} />
        <Route path="objects/run/:phase" element={<ThingRunScreen />} />
        <Route path="objects/:id" element={<ThingPageScreen />} />
        <Route path="more" element={<More />} />
        <Route path="messages" element={<Messages />} />
        {/* A home pays too (owner, 2026-10-14): the card ask and the lock send its owner here, then Stripe sends them back. */}
        <Route path="billing" element={<RequireRole min="owner"><PlanBillingPage /></RequireRole>} />
        <Route path="billing/cancel" element={<RequireRole min="owner"><CancelPlanPage /></RequireRole>} />
        <Route path="billing/back" element={<RequireRole min="owner"><WinbackPage /></RequireRole>} />
        <Route path="billing/start" element={<RequireRole min="owner"><BillingStart /></RequireRole>} />
        <Route path="billing/done" element={<RequireRole min="owner"><BillingDone /></RequireRole>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Everything after sign-in. */
export function AppRoutes() {
  const s = useSessionState();
  const loc = useLocation();
  const kiosk = useKiosk();
  const have = usePendingHave();
  const buyer = useBuyIntent();
  const finish = useFinishSetup(s.org?.id);
  // Someone on a team who isn't its owner or admin can't add the card: the site's buy intent is let go (they still get in).
  const cannotBuy = !!buyer.pending && !!s.org && !isHomeOrg(s.org) && !!s.can && !s.can("admin");
  useEffect(() => { if (cannotBuy) buyer.clear(); }, [cannotBuy]); // eslint-disable-line react-hooks/exhaustive-deps
  // The "We have this" screen stays up after it saves (clearing the stored intent must not take its "Added" screen away) until the person leaves it.
  const [arrive, setArrive] = useState<PendingHave | null>(null);
  useEffect(() => { if (have.pending && !arrive) setArrive(have.pending); }, [have.pending]); // eslint-disable-line react-hooks/exhaustive-deps
  if (s.state.loading && !s.state.me) return <div className="boot"><Skeleton rows={3} label="Opening CleanCleaver" /></div>;
  // A state's rules could not be fetched (core states/loaders.ts): say so with Try again, and let the person stay in the workspace they were in.
  if (s.state.errorKind === "states") return (
    <div className="boot"><div style={{ width: "min(520px, 100%)", display: "grid", gap: "var(--s3)" }} data-testid="states-failed">
      <ErrorBox error={s.state.error} onRetry={() => void s.reload(s.state.retryOrg)} />
      {s.state.me && s.state.orgId && s.state.orgId !== s.state.retryOrg && <Button variant="quiet" onClick={() => void s.reload(s.state.orgId)}>Stay where I was</Button>}
    </div></div>
  );
  if (s.state.error && !s.state.me) return <div className="boot"><div style={{ width: "min(520px, 100%)" }}><ErrorBox error={`We couldn't reach CleanCleaver. ${s.state.error}`} onRetry={() => void s.reload()} /></div></div>;

  // Invite links work whether or not the person already belongs to a business.
  if (loc.pathname.startsWith("/invite/")) return <Routes><Route path="/invite/:inviteId" element={<InviteAccept />} /></Routes>;
  if (loc.pathname === "/join") return <Routes><Route path="/join" element={<JoinCode />} /></Routes>;
  // "Let Claude log for you?" (screens/Connect.tsx): an AI app's sign-in, whatever workspace is open. Never on the shop tablet,
  // which is signed in as someone else for everyone at the counter.
  if (loc.pathname === "/connect") return kiosk
    ? <div className="boot"><div style={{ width: "min(520px, 100%)" }}><ErrorBox error="Connect an AI app from your own phone or computer, not the shop tablet." /></div></div>
    : <Suspense fallback={<div className="boot"><Skeleton rows={3} label="Opening" /></div>}><Connect /></Suspense>;
  // A printed sheet's QR (docs/PUBLIC_SHEET_SCAN.md): a signed-in person on their own team's sheet gets the member flow.
  if (loc.pathname === "/s/about") return <Routes><Route path="/s/about" element={<SheetAbout />} /></Routes>;
  if (loc.pathname.startsWith("/s/")) return <Routes><Route path="/s/:code" element={<SignedInSheet />} /></Routes>;
  // A food label's QR (core foodlabel.ts), scanned while signed out and signed in through another page: open it now, once.
  const labelLater = pendingLabel();
  if (labelLater && !loc.pathname.startsWith("/f/") && s.org && !kiosk) return <Navigate to={`/f/${labelLater}`} replace />;
  // A visitor's guide link (docs/OBJECTS.md) opens the same for a signed-in person, whatever their workspaces.
  if (loc.pathname.startsWith("/guide/")) return <Routes><Route path="/guide/:token" element={<PublicGuideScreen />} /></Routes>;
  // A prospect's "See your place" link (routes/prep.ts) opens the same for someone signed in, with "Make it yours".
  if (loc.pathname.startsWith("/see/")) return <Suspense fallback={<div className="boot"><Skeleton rows={3} label="Opening your place" /></div>}><Routes><Route path="/see/:token" element={<SeePlaceScreen />} /></Routes></Suspense>;
  // The customer allergen menu's QR (routes/allergens.ts) opens the same public page for anyone signed in.
  if (loc.pathname.startsWith("/allergen-menu/")) return <Routes><Route path="/allergen-menu/:orgId/:locationId" element={<PublicAllergenMenuScreen />} /></Routes>;
  // A site buy button (lib/buyIntent.ts): the short "Set up to buy" step instead of the full setup, then Stripe (screens/BuySetup.tsx).
  // A sample-café sign-up with something tried (lib/demoCarry.ts): the normal setup, preset with what they explored ("to confirm").
  if (!s.org || (buyer.pending && isHomeOrg(s.org) && loc.pathname !== "/buy/card")) {
    if (buyer.pending && loc.pathname !== "/buy/card") return <BuySetup intent={buyer.pending} />;
    if (!s.org) { const carry = demoCarry(); return <Routes><Route path="*" element={<Setup carry={carryMeaningful(carry) ? carry : undefined} />} /></Routes>; }
  }
  // The buyer's card step and the rest of their setup after Stripe: full screen, before the frame (never behind a wall: each has a way on).
  if (loc.pathname === "/buy/card") return <BuyCard intent={buyer.pending} />;
  if (loc.pathname === "/setup/finish" && finish) return <Setup resume={finish} />;
  // A signed-in owner who tapped a buy button on the site: straight to the card step for the business they have.
  if (buyer.pending && s.can?.("admin") && !isHomeOrg(s.org)) return <Navigate to="/buy/card" replace />;
  // Just joined: "Do you have a food handler permit?" comes before the check-in (card capture, then the house rules).
  if (loc.pathname === "/welcome") return <Routes><Route path="/welcome" element={<Welcome />} /></Routes>;
  // The phone's "Set up a business" opens /?setup=business here; someone who already has a business gets the same setup.
  if (new URLSearchParams(loc.search).get("setup") === "business" && !loc.pathname.startsWith("/workspaces/new")) return <Setup start="business" />;
  // The shop tablet (ONBOARDING J4): only name tiles and check-ins until a manager leaves tablet mode.
  if (kiosk && kiosk.orgId === s.org.id) return <Kiosk config={kiosk} />;
  // A food label's QR (core foodlabel.ts): the food with its three buttons, for a member of its business only (the server decides).
  if (loc.pathname.startsWith("/f/")) return <Suspense fallback={<div className="boot"><Skeleton rows={3} label="Opening the label" /></div>}><Routes><Route path="/f/:token" element={<ScanLabel />} /></Routes></Suspense>;
  // "We have this" from the site (lib/haveIntent.ts): straight in when they were already signed in, else one "Keep it?" now that they have a place.
  // Never in front of sign-up or setup (those return above), and "Not now" lets go of it.
  // A guided link from our team (lib/goIntent.ts): opens its place once, now that the person is in a workspace.
  const go = pendingGo();
  if (go) return <GoNow go={go} />;
  const showArrive = arrive ?? have.pending;
  if (showArrive && !loc.pathname.startsWith("/workspaces/new")) return <HaveArrive pending={showArrive} clear={have.clear} onLeave={() => setArrive(null)} />;
  // "Fix and log it now" from the site (lib/fixIntent.ts; core fixgo.ts): a business member who opens the app on Today goes straight to
  // the fix, once per tap on the site. (Setup lands on /fix/<item> itself; the "Do you work at or own it?" card keeps the intent.)
  const fixGo = loc.pathname === "/" && !isHomeOrg(s.org) ? pendingFix() : null;
  if (fixGo && !fixGo.home && !fixShown()) return <Navigate to={`/fix/${fixGo.item}`} replace />;
  if (isHomeOrg(s.org)) return <HomeRoutes />;
  // The plan gate (components/PlanGate.tsx; the server refuses the same writes): one lock screen, except the pages that stay open.
  if (s.plan?.level === "locked" && !lockedPathOk(loc.pathname)) return <LockScreen plan={s.plan} />;

  return (
    <Routes>
      {/* Set up another business or your home (docs/WORKSPACES.md): the first-run setup, full screen. */}
      <Route path="workspaces/new/business" element={<Setup start="business" />} />
      <Route path="workspaces/new/home" element={<Setup start="home" />} />
      <Route element={<Layout />}>
        {/* A place getting ready to open gets "Getting ready" (no late alarms, review A11); else the feed. */}
        <Route index element={<TodayIndex />} />
        <Route path="start/have" element={<RequireRole min="manager"><HaveWalk /></RequireRole>} />
        <Route path="start/stage" element={<RequireRole min="admin"><StageQuestion /></RequireRole>} />
        <Route path="start/open" element={<RequireRole min="admin"><WeAreOpen /></RequireRole>} />
        {/* "Is this you?": what setup filled in from the record, the first 3 things, and the trial ask (core isthisyou.ts). */}
        <Route path="start/ready" element={<SetupReady />} />
        <Route path="inspection" element={<LastInspectionLazy />} />
        <Route path="fix/:item" element={<FixArrive />} />
        <Route path="workspaces" element={<Workspaces />} />
        <Route path="invite-business" element={<InviteBusiness />} />
        {/* Set up a place for someone else (routes/prepMine.ts; core prep.ts): any signed-in person, their own preps. */}
        <Route path="prep" element={<PrepPlaceScreen />} />
        <Route path="prep/:orgId" element={<PrepPlaceScreen />} />
        <Route path="today" element={<Today />} />
        <Route path="carrying" element={<Carrying />} />
        <Route path="steps/:procedureId" element={<StepsLink />} />
        <Route path="team" element={<RequireRole min="manager"><Team /></RequireRole>} />
        <Route path="team/logs" element={<RequireRole min="manager"><LogReviewScreen /></RequireRole>} />
        <Route path="team/day" element={<CloseOfDayScreen />} />
        <Route path="activity" element={<ActivityScreen />} />
        <Route path="activity/:employeeId" element={<RequireRole min="manager"><ActivityScreen /></RequireRole>} />
        <Route path="points" element={<PointsScreen />} />
        <Route path="me" element={<Me />} />
        <Route path="me/notifications" element={<Notifications />} />
        <Route path="reach" element={<Reach />} />
        <Route path="do" element={<Do />} />
        <Route path="do/fix/:entryId" element={<Fix />} />
        {/* Allergens (core allergens.ts): the cook's one-tap safe prep, the owner's confirm, the printable menu. */}
        <Route path="allergy-order" element={<AllergyOrder />} />
        <Route path="recall" element={<RequireRole min="manager"><Recall /></RequireRole>} />
        <Route path="allergens/setup" element={<RequireRole min="admin"><AllergenSetup /></RequireRole>} />
        <Route path="allergens/setup/:id" element={<RequireRole min="admin"><AllergenDish /></RequireRole>} />
        <Route path="allergens/menu" element={<RequireRole min="manager"><AllergenMenuScreen /></RequireRole>} />
        {/* Photograph your menu (core menuphoto.ts): read the dishes, a few quick questions, the owner confirms each dish. */}
        <Route path="allergens/photo" element={<RequireRole min="admin"><MenuPhoto /></RequireRole>} />
        <Route path="report" element={<Report />} />
        <Route path="paper" element={<Paper />} />
        <Route path="paper/review" element={<RequireRole min="manager"><Review /></RequireRole>} />
        <Route path="check-in" element={<CheckIn />} />
        <Route path="learn" element={<Learn />} />
        <Route path="learn/lesson/:lessonId" element={<LessonPage />} />
        <Route path="learn/sign/:ackId" element={<SignPage />} />
        <Route path="learn/library" element={<Library />} />
        <Route path="learn/library/:topicId" element={<LibraryTopic />} />
        <Route path="learn/how/:templateId" element={<HowToPage />} />
        <Route path="learn/surfaces" element={<Surfaces />} />
        <Route path="learn/surfaces/:surfaceId" element={<SurfacePage />} />
        <Route path="learn/stains" element={<CleanupList kind="stains" />} />
        <Route path="learn/stains/:slug" element={<CleanupPage kind="stains" />} />
        <Route path="learn/spills" element={<CleanupList kind="spills" />} />
        <Route path="learn/spills/:slug" element={<CleanupPage kind="spills" />} />
        <Route path="learn/playbook" element={<Playbook />} />
        <Route path="people" element={<RequireRole min="manager"><People /></RequireRole>} />
        <Route path="people/add" element={<RequireRole min="manager"><AddPerson /></RequireRole>} />
        <Route path="people/:id" element={<RequireRole min="manager"><Person /></RequireRole>} />
        <Route path="open" element={<RequireRole min="manager"><Opening /></RequireRole>} />
        <Route path="prove" element={<RequireRole min="manager"><Prove /></RequireRole>} />
        <Route path="audit-trail" element={<RequireRole min="manager"><AuditTrail /></RequireRole>} />
        <Route path="signs" element={<RequireRole min="manager"><Signs /></RequireRole>} />
        <Route path="health-agreement" element={<RequireRole min="manager"><HealthAgreementRoster /></RequireRole>} />
        <Route path="insights" element={<RequireRole min="manager"><Insights /></RequireRole>} />
        <Route path="health" element={<RequireRole min="manager"><HealthScreen /></RequireRole>} />
        {/* Settings (hours, locations): an owner, or a manager/admin the owner shares Settings with (core ownership.ts). */}
        <Route path="settings" element={<RequireSwitch sw="settings"><Settings /></RequireSwitch>} />
        <Route path="settings/locations/new" element={<RequireSwitch sw="settings"><AddLocation /></RequireSwitch>} />
        {/* Google sends the owner back here after "Connect your Google Maps profile" (docs/GOOGLE-BUSINESS-PROFILE.md). */}
        <Route path="google/callback" element={<RequireRole min="admin"><GoogleCallback /></RequireRole>} />
        {/* Stripe Checkout: the start page and where Stripe sends people back (docs/PAYMENTS.md). */}
        {/* Plan and billing: its own page, nothing unrelated on it (owner, 2026-10-07). */}
        {/* Billing: an owner, or a manager/admin the owner lets handle billing; whoever added the card still sees the cost. */}
        <Route path="billing" element={<RequireSwitch sw="billing"><PlanBillingPage /></RequireSwitch>} />
        <Route path="billing/cancel" element={<RequireSwitch sw="billing"><CancelPlanPage /></RequireSwitch>} />
        <Route path="billing/back" element={<RequireSwitch sw="billing"><WinbackPage /></RequireSwitch>} />
        <Route path="billing/start" element={<RequireSwitch sw="billing"><BillingStart /></RequireSwitch>} />
        <Route path="billing/done" element={<RequireSwitch sw="billing"><BillingDone /></RequireSwitch>} />
        {/* Who may do what, billing admins, passing the business on (owner); a manager sees their own switches. */}
        <Route path="people/permissions" element={<RequireRole min="manager"><Permissions /></RequireRole>} />
        {/* The new owner's Accept (from the email or the note at the top). */}
        <Route path="ownership/accept" element={<OwnershipAccept />} />
        {/* Staff (CONTENT_ADMINS) see the inbox; owners/admins their own questions; the page decides (docs/CONTENT_PIPELINE.md). */}
        <Route path="content" element={<Content />} />
        {/* The county waitlist's "Open a county" (docs/WAITLIST.md): the server gates it (DATA_ADMINS, a verified sign-in). */}
        <Route path="admin/waitlist" element={<WaitlistAdmin />} />
        {/* Your data and privacy (routes/compliance.ts); the staff page answers 404 to anyone not on DATA_ADMINS. */}
        <Route path="privacy" element={<Privacy />} />
        <Route path="admin/compliance" element={<Compliance />} />
        {/* The admin Data dashboard: the server gates it (DATA_ADMINS, verified sign-in); the link shows only when me.dataAdmin. */}
        <Route path="admin/data" element={<DataDashboard />} />
        <Route path="more" element={<More />} />
        <Route path="messages" element={<Messages />} />
        <Route path="kits" element={<Kits />} />
        <Route path="kits/:kitId" element={<Kits />} />
        <Route path="food" element={<Food />} />
        <Route path="food/label" element={<LabelFood />} />
        {/* A food tapped in Soonest to expire: Threw it out · Opened it · Moved it (screens/ScanLabel.tsx). */}
        <Route path="food/act/:id" element={<FoodAct />} />
        {/* Cooling batches: Start cooling, the 2- and 6-hour readings, the fix (core cooling.ts). */}
        <Route path="food/cooling" element={<Cooling />} />
        <Route path="order" element={<Order />} />
        <Route path="guest-notes" element={<GuestNotes />} />
        <Route path="supplies/setup" element={<RequireRole min="manager"><SuppliesSetup /></RequireRole>} />
        <Route path="supplies" element={<SuppliesHub />} />
        <Route path="supplies/kit" element={<ShopKit />} />
        <Route path="products" element={<Products />} />
        <Route path="supplies/item/:key" element={<SupplyPage />} />
        <Route path="supplies/check" element={<StockCheckScreen />} />
        <Route path="supplies/buy" element={<BuyByRoomScreen />} />
        <Route path="places" element={<Places />} />
        <Route path="places/:id" element={<PlaceObjectPage />} />
        <Route path="walk/review" element={<WalkReviewScreen />} />
        <Route path="walk" element={<RoomWalkScreen />} />
        <Route path="objects" element={<ThingsScreen />} />
        <Route path="objects/states" element={<RequireRole min="manager"><ThingStatesScreen /></RequireRole>} />
        <Route path="objects/guide" element={<ThingGuideScreen />} />
        <Route path="objects/run/:phase" element={<ThingRunScreen />} />
        <Route path="objects/:id" element={<ThingPageScreen />} />
        <Route path="rooms" element={<RoomBoardScreen />} />
        {/* AI RoomCheck prototype (docs/AI-ROOMCHECK.md): the API answers 404 unless AI_ROOMCHECK=on, and the screen says so. */}
        <Route path="rooms/ai-check" element={<AiRoomCheckScreen />} />
        <Route path="rooms/check/:room" element={<RoomCheckScreen />} />
        <Route path="rooms/check/:room/contents" element={<RoomContentsScreen />} />
        <Route path="clean" element={<CleanPlaceScreen />} />
        {/* Old paths (UX_SCOPE G17 moved Snap and Print under Paper, Playbook under Learn). */}
        <Route path="snap" element={<Navigate to="/paper" replace />} />
        <Route path="snap/review" element={<Navigate to="/paper/review" replace />} />
        <Route path="print" element={<Navigate to="/paper#print" replace />} />
        <Route path="playbook" element={<Navigate to="/learn/playbook" replace />} />
        <Route path="setup" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Opens a guided link's place once and marks the message clicked (the server checks it is this person's message). */
function GoNow({ go }: { go: PendingGo }) {
  const api = useApi();
  const s = useSession();
  const nav = useNavigate();
  useEffect(() => {
    clearGo();
    if (go.messageId) void api.markTeamMessage(s.org.id, go.messageId, "clicked", isHomeOrg(s.org)).catch(() => undefined);
    nav(go.path, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export function Gate() {
  const auth = useAuth();
  // An intent waiting while nobody is signed in: when they do sign in or up it is the "Keep it?" path (lib/haveIntent.ts).
  useEffect(() => { if (auth.status === "signedOut") markSignedOut(); }, [auth.status]);
  if (auth.status === "loading") return <div className="boot"><Skeleton rows={3} label="Opening CleanCleaver" /></div>;
  // A visitor's guide link needs no account (routes/things.ts GET /guide/:token).
  if (auth.status === "signedOut" && /^\/guide\/[^/]+$/.test(window.location.pathname.replace(/^\/app(?=\/)/, ""))) return <ApiProvider><PublicGuideGate /></ApiProvider>;
  // "See your place" needs no account to view (routes/prep.ts GET /see/:token); "Make it yours" asks to sign in.
  if (auth.status === "signedOut" && /^\/see\/[^/]+$/.test(window.location.pathname.replace(/^\/app(?=\/)/, ""))) return <ApiProvider><Suspense fallback={<div className="boot"><Skeleton rows={3} label="Opening your place" /></div>}><Routes><Route path="/see/:token" element={<SeePlaceScreen />} /></Routes></Suspense></ApiProvider>;
  // A printed sheet's QR needs no account either (routes/publicSheet.ts): one photo, then thanks.
  if (auth.status === "signedOut" && /^\/s\/[^/]+$/.test(window.location.pathname.replace(/^\/app(?=\/)/, ""))) return <ApiProvider><PublicSheetGate /></ApiProvider>;
  // The customer allergen menu needs no account (routes/allergens.ts): on only when the owner turned it on.
  if (auth.status === "signedOut" && /^\/allergen-menu\/[^/]+\/[^/]+$/.test(window.location.pathname.replace(/^\/app(?=\/)/, ""))) return <ApiProvider><PublicAllergenMenuGate /></ApiProvider>;
  // A food label scanned while signed out: sign in first (never the food); the label opens after (lib/labelScan.ts).
  if (auth.status === "signedOut") { const t = labelTokenInPath(window.location.pathname); if (t) rememberLabel(t); }
  if (auth.status === "signedOut") return <ScreenBoundary screen="sign-in"><SignIn /></ScreenBoundary>;
  return (
    <ApiProvider>
      <SessionProvider>
        <Outbox who={auth.user?.email}>
          {/* The sample café's 18-tap gate (components/DemoTapGate.tsx): a real business renders straight through. */}
          <DemoTapGate><OuterBoundary><AppRoutes /></OuterBoundary></DemoTapGate>
          <LinkReportHost />
          <VoteHost />
          <ConsentHost />
        </Outbox>
      </SessionProvider>
    </ApiProvider>
  );
}

/** A customer's answer from a place page (lib/voteIntent.ts), sent once now that they are signed in; a toast says what happened. */
function VoteHost() {
  const api = useApiMaybe();
  const toast = useToast();
  useEffect(() => {
    const v = api ? pendingVote() : null;
    if (!api || !v) return;
    clearVote();
    void api.placeVote(v).then((r) => toast.show(`${VOTE_WORDS.thanksH} ${r.line}`)).catch((e: unknown) => toast.show(e instanceof Error ? e.message : VOTE_WORDS.bad));
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** The guide page for someone signed out: the demo (mock mode) needs a sign-in to have an API at all. */
function PublicGuideGate() {
  const api = useApiMaybe();
  if (!api) return <ScreenBoundary screen="sign-in"><SignIn /></ScreenBoundary>;
  return <ScreenBoundary screen="guide"><Suspense fallback={<div className="boot"><Skeleton rows={3} label="Opening the guide" /></div>}><Routes><Route path="/guide/:token" element={<PublicGuideScreen />} /></Routes></Suspense></ScreenBoundary>;
}

/** The sheet page for someone signed out. The demo (mock mode) has no server to ask until someone signs in. */
function PublicSheetGate() {
  const api = useApiMaybe();
  if (!api) return <ScreenBoundary screen="sign-in"><SignIn /></ScreenBoundary>;
  return <ScreenBoundary screen="sheet"><Suspense fallback={<div className="boot"><Skeleton rows={3} label="Opening the sheet" /></div>}><Routes><Route path="/s/about" element={<SheetAbout />} /><Route path="/s/:code" element={<PublicSheetScreen />} /></Routes></Suspense></ScreenBoundary>;
}

/** The allergen menu for someone signed out. The demo (mock mode) has no server to ask until someone signs in. */
function PublicAllergenMenuGate() {
  const api = useApiMaybe();
  if (!api) return <ScreenBoundary screen="sign-in"><SignIn /></ScreenBoundary>;
  return <ScreenBoundary screen="allergen-menu"><Suspense fallback={<div className="boot"><Skeleton rows={3} label="Opening the menu" /></div>}><Routes><Route path="/allergen-menu/:orgId/:locationId" element={<PublicAllergenMenuScreen />} /></Routes></Suspense></ScreenBoundary>;
}

/** Save later (lib/outbox.tsx) for this person, once the API client exists. */
function Outbox({ who, children }: { who?: string; children: ReactNode }) {
  const api = useApiMaybe();
  if (!api || !who) return <>{children}</>;
  return <OutboxProvider api={api} who={who}>{children}</OutboxProvider>;
}

/** The screens outside the frame (setup, invite links, the tablet) get the same card; it resets when the path changes. */
function OuterBoundary({ children }: { children: ReactNode }) {
  const loc = useLocation();
  return <ScreenBoundary resetKey={loc.pathname} screen={screenName(loc.pathname)}><Suspense fallback={<div className="boot"><Skeleton rows={3} label="Opening CleanCleaver" /></div>}>{children}</Suspense></ScreenBoundary>;
}

/** The cookie banner (only in a build that sends analytics); a signed-in choice is also logged as the usage_data consent. */
function ConsentHost() {
  const api = useApiMaybe();
  const loc = useLocation();
  // Anonymous screen views (core measure.ts): no cookie, no id, ids taken out of the path, health screens never; the server only counts.
  useEffect(() => {
    if (config.apiMode !== "http" || config.demo) return;
    anonMeasure({ surface: "web", kind: "view", path: loc.pathname, width: window.innerWidth, coarse: window.matchMedia?.("(pointer: coarse)").matches === true });
  }, [loc.pathname]);
  return <CookieBanner record={api ? (a) => api.setConsent("usage_data", a) : undefined} />;
}
const anonMeasure = makeMeasure((body) => { try { void fetch(`${config.apiUrl}/measure/anon`, { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true, credentials: "omit" }).catch(() => undefined); } catch { /* counting never breaks a screen */ } });

export function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Gate />
      </ToastProvider>
    </AuthProvider>
  );
}
